import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Layout from '../Layout';
import Plot from 'react-plotly.js';
import {
    listBaskets, createBasket, fetchCompetitorData,
    getCompetitorData, deleteBasket, patchBasket, updateBasket,
    parseCsvFile, parseAsinListCsv, toggleBasketAutoFetch, fetchCompetitorDataFromList, getTrackingImageProxyUrl,
    backfillBasket, getJungleScoutTokens, syncCompetitorData
} from '../../services/trackingService';
import 'bootstrap/dist/css/bootstrap.min.css';

/* ───────────────────────────────────────────── */
/* Tracking Overview — port of tracking2 home.py */
/* ───────────────────────────────────────────── */

const ProductCard = ({ row }) => {
    const [showDetails, setShowDetails] = useState(false);
    const proxyUrl = row.product_photo ? getTrackingImageProxyUrl(row.product_photo) : '';
    const [imgSrc, setImgSrc] = useState(proxyUrl);

    useEffect(() => {
        setImgSrc(row.product_photo ? getTrackingImageProxyUrl(row.product_photo) : '');
    }, [row.product_photo]);

    const fmtPrice = (p) => p != null ? `$${Number(p).toFixed(2)}` : '—';
    const fmtNum = (n) => n != null ? Number(n).toLocaleString() : '—';

    return (
        <div className="card h-100 shadow-sm">
            {imgSrc ? (
                <img
                    src={imgSrc}
                    className="card-img-top p-2"
                    alt={row.asin}
                    style={{ height: '180px', objectFit: 'contain' }}
                    onError={() => {
                        if (imgSrc !== row.product_photo) {
                            setImgSrc(row.product_photo || '');
                        } else {
                            setImgSrc('');
                        }
                    }}
                />
            ) : (
                <div className="d-flex align-items-center justify-content-center bg-light" style={{ height: '180px' }}>
                    <i className="bi bi-box-seam fs-1 text-muted"></i>
                </div>
            )}
            <div className="card-body p-3 d-flex flex-column">
                <div className="mb-2">
                    <small className="text-muted fw-bold">{row.brand}</small>
                    <small className="text-muted mx-1">—</small>
                    <small className="text-primary font-monospace">{row.asin}</small>
                </div>

                <h6 className="card-title text-truncate-2" title={row.product_title} style={{ minHeight: '40px', fontSize: '0.9rem', lineHeight: '1.3' }}>
                    {row.product_title || 'No title available'}
                </h6>

                <div className="mt-auto pt-2">
                    <a href={row.product_url} target="_blank" rel="noopener noreferrer" className="d-block text-decoration-none mb-2 small">
                        <i className="bi bi-box-arrow-up-right me-1"></i> Open product page
                    </a>

                    <button className={`btn btn-sm w-100 ${showDetails ? 'btn-secondary' : 'btn-outline-primary'}`}
                        onClick={() => setShowDetails(!showDetails)}
                    >
                        {showDetails ? 'Hide Details' : 'Details'} <i className={`bi bi-chevron-${showDetails ? 'up' : 'down'}`}></i>
                    </button>
                </div>

                {showDetails && (
                    <div className="mt-3 pt-2 border-top small text-start bg-light rounded p-2">
                        <div className="mb-1"><strong>Price:</strong> {fmtPrice(row.product_price)}</div>
                        {row.product_original_price && (
                            <div className="mb-1"><strong>Original:</strong> <span className="text-decoration-line-through text-muted">{fmtPrice(row.product_original_price)}</span></div>
                        )}
                        <div className="mb-1"><strong>Rating:</strong> ⭐ {row.product_star_rating || '-'} ({fmtNum(row.product_num_ratings)})</div>
                        {row.discount && <div className="mb-1"><strong>Discount:</strong> <span className="text-danger">-{Number(row.discount)}%</span></div>}
                        {row.sales_volume && <div className="mb-1"><strong>Sales:</strong> {row.sales_volume}</div>}
                        {row.rank_value && <div className="mb-1"><strong>Rank:</strong> #{fmtNum(row.rank_value)}</div>}
                        {row.sub_category_name && <div className="mb-1 text-muted fst-italic">{row.sub_category_name}</div>}
                    </div>
                )}
            </div>
        </div>
    );
};

function TrackingOverview() {
    const [baskets, setBaskets] = useState([]);
    const [selectedBasket, setSelectedBasket] = useState(null);
    const [data, setData] = useState([]);
    const [period, setPeriod] = useState('week');
    const [selectedBrands, setSelectedBrands] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [dateRange, setDateRange] = useState({ from: '', to: '' });
    const [csvName, setCsvName] = useState(null);
    const [csvFile, setCsvFile] = useState(null);
    
    // Quick filter states
    const [quickYear, setQuickYear] = useState('');
    const [showInfoTooltip, setShowInfoTooltip] = useState(false);
    const [showSyncTooltip, setShowSyncTooltip] = useState(false);
    const [showBackfillTooltip, setShowBackfillTooltip] = useState(false);

    // New basket form
    const [showNewBasket, setShowNewBasket] = useState(false);
    const [newName, setNewName] = useState('');
    const [newAsins, setNewAsins] = useState('');
    const [newCountry, setNewCountry] = useState('US');
    const [importHistory, setImportHistory] = useState(false);
    const [backfilling, setBackfilling] = useState(false);
    const [tokens, setTokens] = useState(null);
    const [showBackfillModal, setShowBackfillModal] = useState(false);
    const [backfillResult, setBackfillResult] = useState(null); // { success, message }
    const [syncResult, setSyncResult] = useState(null); // { message, synced, skipped, failed }

    const [showEditBasket, setShowEditBasket] = useState(false);
    const [editBasketId, setEditBasketId] = useState(null);
    const [editName, setEditName] = useState('');
    const [editAsins, setEditAsins] = useState('');
    const [editCountry, setEditCountry] = useState('US');

    const accountId = localStorage.getItem('selectedAccountValue') || '';

    // Load JS token budget status
    const loadTokens = useCallback(async () => {
        try {
            const res = await getJungleScoutTokens();
            if (res && res.remaining !== undefined) {
                setTokens(res);
            }
        } catch (err) {
            console.error('Error loading tokens:', err);
        }
    }, []);

    useEffect(() => {
        loadTokens();
    }, [loadTokens]);

    // Load baskets
    const loadBaskets = useCallback(async () => {
        if (!accountId) return;
        try {
            const res = await listBaskets(accountId);
            setBaskets(res.data || []);
        } catch (err) {
            console.error('Error loading baskets:', err);
        }
    }, [accountId]);

    useEffect(() => { loadBaskets(); }, [loadBaskets]);

    // Load data when basket selected (skip for CSV baskets — data already loaded)
    const loadData = useCallback(async () => {
        if (!selectedBasket || !accountId || selectedBasket.id === 'csv') return;
        setLoading(true);
        setError('');
        try {
            const res = await getCompetitorData(selectedBasket.id, accountId);
            const rows = res.data || [];
            setData(rows);
            const brands = [...new Set(rows.map(r => r.brand).filter(Boolean))].sort();
            setSelectedBrands(brands);
            // Set date range to current year by default
            const dates = rows.map(r => r.date).filter(Boolean).sort();
            if (dates.length > 0) {
                const currentYear = new Date().getFullYear();
                const jan1 = `${currentYear}-01-01`;
                const fromDate = dates[0] < jan1 ? jan1 : dates[0];
                setDateRange({ from: fromDate, to: dates[dates.length - 1] });
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [selectedBasket, accountId]);

    useEffect(() => { loadData(); }, [loadData]);

    // Create basket
    const handleCreateBasket = async (e) => {
        e.preventDefault();
        const asins = newAsins.split(/[\n,]+/).map(s => s.trim()).filter(Boolean);
        if (!newName || asins.length === 0) return;
        try {
            await createBasket({ name: newName, asins, country: newCountry, accountId, importHistory });
            setNewName(''); setNewAsins(''); setShowNewBasket(false); setImportHistory(false);
            loadBaskets();
            loadTokens();
        } catch (err) {
            setError(err.message);
        }
    };

    // Manual backfill for existing baskets
    const handleManualBackfill = () => {
        if (!selectedBasket || selectedBasket.id === 'csv') return;
        if (!accountId) {
            setError('Please select an Amazon Ads account before requesting backfill.');
            return;
        }
        setBackfillResult(null);
        setShowBackfillModal(true);
    };

    const confirmBackfill = async () => {
        setShowBackfillModal(false);
        setBackfilling(true);
        setError('');
        try {
            const res = await backfillBasket(selectedBasket.id, accountId);
            setBackfillResult({ success: true, message: res.message || 'Historical data has been imported successfully.' });
            await loadData();
            await loadTokens();
        } catch (err) {
            setBackfillResult({ success: false, message: err?.response?.data?.error || err.message || 'An error occurred while importing historical data.' });
        } finally {
            setBackfilling(false);
        }
    };

    // Fetch fresh data
    const handleFetchData = async () => {
        if (!selectedBasket) return;
        if (selectedBasket.id !== 'csv' && !accountId) {
            setError('Please select an Amazon Ads account before fetching basket data.');
            return;
        }

        setLoading(true);
        setError('');
        setSyncResult(null);
        try {
            if (selectedBasket.id === 'csv') {
                if (!Array.isArray(selectedBasket.asins) || selectedBasket.asins.length === 0) {
                    setError('CSV list has no ASINs. Please edit or upload a valid CSV.');
                    return;
                }

                const res = await fetchCompetitorDataFromList(
                    selectedBasket.asins,
                    accountId,
                    selectedBasket.country || 'US'
                );
                const rows = res.data || [];
                console.log('[TrackingOverview] CSV fetch diagnostics:', res?.diagnostics || null);
                if (Array.isArray(res?.diagnostics?.failedItems) && res.diagnostics.failedItems.length > 0) {
                    console.table(res.diagnostics.failedItems);
                }
                setData(rows);

                const brands = [...new Set(rows.map(r => r.brand).filter(Boolean))].sort();
                setSelectedBrands(brands);

                const dates = rows.map(r => r.date).filter(Boolean).sort();
                if (dates.length > 0) {
                    const currentYear = new Date().getFullYear();
                    const jan1 = `${currentYear}-01-01`;
                    const fromDate = dates[0] < jan1 ? jan1 : dates[0];
                    setDateRange({ from: fromDate, to: dates[dates.length - 1] });
                }
                return;
            }

            // Use delta sync for DB-backed baskets instead of full fetch
            const res = await syncCompetitorData(selectedBasket.id, accountId);
            setSyncResult({
                message: res.message,
                synced: res.synced,
                skipped: res.skipped,
                failed: res.failed
            });
            await loadData();
        } catch (err) {
            setError(err?.response?.data?.error || err?.response?.data?.message || err.message || 'Failed to fetch competitor data');
        } finally {
            setLoading(false);
        }
    };

    // Delete basket
    const handleDeleteBasket = async (id) => {
        if (!window.confirm('Delete this basket and its data')) return;
        try {
            await deleteBasket(id);
            if (selectedBasket?.id === id) { setSelectedBasket(null); setData([]); }
            loadBaskets();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleToggleAutoFetch = async (basketId, e) => {
        if (e) e.stopPropagation();
        try {
            const res = await toggleBasketAutoFetch(basketId);
            const updated = res?.data;
            if (!updated) {
                await loadBaskets();
                return;
            }

            setBaskets(prev => prev.map(b => b.id === basketId ? { ...b, ...updated } : b));
            if (selectedBasket?.id === basketId) {
                setSelectedBasket(prev => prev ? { ...prev, ...updated } : prev);
            }
        } catch (err) {
            setError(err.message || 'Failed to toggle auto-fetch');
        }
    };

    const openEditBasket = (basket) => {
        setEditBasketId(basket.id);
        setEditName(basket.name || '');
        setEditAsins(Array.isArray(basket.asins) ? basket.asins.join('\n') : '');
        setEditCountry(basket.country || 'US');
        setShowEditBasket(true);
    };

    const handleOpenEditFromSelection = () => {
        if (!selectedBasket) return;
        openEditBasket(selectedBasket);
    };

    const handleUpdateBasket = async (e) => {
        e.preventDefault();
        const asins = editAsins
            .split(/[\n,]+/)
            .map(s => s.trim().toUpperCase())
            .filter(Boolean);

        if (!editBasketId || !editName || (editBasketId !== 'csv' && asins.length === 0)) return;

        try {
            if (editBasketId === 'csv') {
                setSelectedBasket(prev => ({
                    ...prev,
                    name: editName,
                    country: editCountry,
                    asins: asins,
                }));
                setShowEditBasket(false);
                setEditBasketId(null);
                return;
            }

            const res = await updateBasket(editBasketId, {
                name: editName,
                asins,
                country: editCountry,
            });

            const updated = res?.data || {
                id: editBasketId,
                name: editName,
                asins,
                country: editCountry,
            };

            setBaskets(prev => prev.map(b => b.id === editBasketId ? { ...b, ...updated } : b));
            if (selectedBasket?.id === editBasketId) {
                setSelectedBasket(prev => prev ? { ...prev, ...updated } : prev);
            }

            setShowEditBasket(false);
            setEditBasketId(null);
            setEditName('');
            setEditAsins('');
            setEditCountry('US');
            await loadBaskets();
        } catch (err) {
            setError(err.message || 'Failed to update basket');
        }
    };

    // CSV upload
    const handleCsvUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setLoading(true);
        setError('');
        try {
            const parsed = await parseAsinListCsv(file);
            const asins = parsed.data || [];

            if (asins.length === 0) {
                setError('CSV without ASINs in first column. Expected format: ASIN,optional_label');
                return;
            }

            setData([]);
            setCsvName(file.name);
            setCsvFile(file);

            setSelectedBasket({ id: 'csv', name: file.name, asins, country: 'US' });
            setSelectedBrands([]);
            setDateRange({ from: '', to: '' });
        } catch (err) {
            setError('Error parsing CSV: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleClearCsv = () => {
        setCsvName(null);
        setCsvFile(null);
        setSelectedBasket(null);
        setData([]);
        setSelectedBrands([]);
    };

    // ── Derived data ──
    const allBrands = useMemo(() =>
        [...new Set(data.map(r => r.brand).filter(Boolean))].sort(), [data]);

    const availableWeeks = useMemo(() => {
        if (!data || data.length === 0) return [];
        const weeksMap = new Map();
        
        data.forEach(r => {
            if (!r.date) return;
            const w = r.week || r.week_number;
            if (!w) return;
            
            let year = r.date.substring(0, 4);
            const month = r.date.substring(5, 7);
            
            // Fix ISO year boundaries: Jan dates in week 52/53 belong to the previous year's week
            if (month === '01' && (w >= 52)) {
                year = String(Number(year) - 1);
            }
            // Dec dates in week 1 belong to the next year's week
            else if (month === '12' && w === 1) {
                year = String(Number(year) + 1);
            }

            const key = `${year}-${String(w).padStart(2, '0')}`;
            
            if (!weeksMap.has(key)) {
                weeksMap.set(key, { year, week: w, min: r.date, max: r.date });
            } else {
                const current = weeksMap.get(key);
                if (r.date < current.min) current.min = r.date;
                if (r.date > current.max) current.max = r.date;
            }
        });
        
        return Array.from(weeksMap.entries())
            .map(([key, info]) => ({
                key,
                year: info.year,
                label: `Wk ${info.week} of ${info.year} (${info.min} to ${info.max})`,
                minDate: info.min,
                maxDate: info.max
            }))
            .sort((a, b) => b.key.localeCompare(a.key));
    }, [data]);

    const availableYears = useMemo(() => {
        return [...new Set(availableWeeks.map(w => w.year))].sort((a, b) => b.localeCompare(a));
    }, [availableWeeks]);

    const activeQuickYear = quickYear || (availableYears.length > 0 ? availableYears[0] : '');
    const filteredWeeks = availableWeeks.filter(w => w.year === activeQuickYear);

    // Determine current selected week based on dateRange
    const currentWeekValue = availableWeeks.find(w => w.minDate === dateRange.from && w.maxDate === dateRange.to)
        ? `${dateRange.from}|${dateRange.to}`
        : '';

    // Filter by brands and date range
    const filteredData = useMemo(() => {
        let rows = data.filter(r => selectedBrands.includes(r.brand));
        if (dateRange.from) rows = rows.filter(r => r.date >= dateRange.from);
        if (dateRange.to) rows = rows.filter(r => r.date <= dateRange.to);
        return rows;
    }, [data, selectedBrands, dateRange]);

    // ── Highlights (port of highlights_section.py) ──
    const highlights = useMemo(() => {
        if (filteredData.length === 0) return null;

        // Get last period data
        let periodData;
        let label;
        if (period === 'week') {
            const weeks = filteredData.map(r => r.week || r.week_number).filter(Boolean);
            const lastWeek = Math.max(...weeks);
            periodData = filteredData.filter(r => (r.week || r.week_number) === lastWeek);
            label = `week ${lastWeek}`;
        } else {
            const dates = filteredData.map(r => r.date).filter(Boolean).sort();
            const lastDay = dates[dates.length - 1];
            periodData = filteredData.filter(r => r.date === lastDay);
            label = lastDay;
        }

        if (periodData.length === 0) return { label };

        // Discount %
        const withDiscount = periodData.map(r => {
            const origPrice = r.product_original_price;
            const curPrice = r.product_price;
            const discountPct = (origPrice && origPrice > 0) ? ((origPrice - curPrice) / origPrice * 100) : null;
            return { ...r, discount_pct: discountPct };
        });

        // Extremes
        const validDiscounts = withDiscount.filter(r => r.discount_pct != null);
        const highestDisc = validDiscounts.length ? validDiscounts.reduce((a, b) => Number(a.discount_pct) > Number(b.discount_pct) ? a : b) : null;
        const lowestDisc = validDiscounts.length ? validDiscounts.reduce((a, b) => Number(a.discount_pct) < Number(b.discount_pct) ? a : b) : null;

        const validPrices = periodData.filter(r => r.product_price != null);
        const highestPrice = validPrices.length ? validPrices.reduce((a, b) => Number(a.product_price) > Number(b.product_price) ? a : b) : null;
        const lowestPrice = validPrices.length ? validPrices.reduce((a, b) => Number(a.product_price) < Number(b.product_price) ? a : b) : null;

        // Price change extremes
        const validChanges = periodData.filter(r => r.price_change != null);
        const largestChange = validChanges.length ? validChanges.reduce((a, b) => Number(a.price_change) > Number(b.price_change) ? a : b) : null;
        const lowestChange = validChanges.length ? validChanges.reduce((a, b) => Number(a.price_change) < Number(b.price_change) ? a : b) : null;

        // Averages
        const avgDiscount = validDiscounts.length ? (validDiscounts.reduce((s, r) => s + Number(r.discount_pct), 0) / validDiscounts.length) : null;
        const avgPrice = validPrices.length ? (validPrices.reduce((s, r) => s + Number(r.product_price), 0) / validPrices.length) : null;
        const avgChange = validChanges.length ? (validChanges.reduce((s, r) => s + Number(r.price_change), 0) / validChanges.length) : null;

        return {
            label, highestDisc, lowestDisc,
            highestPrice, lowestPrice,
            largestChange, lowestChange,
            avgDiscount, avgPrice, avgChange,
        };
    }, [filteredData, period]);

    // ── Chart traces ──
    const groupKey = period === 'day' ? 'date' : 'week';
    const buildOverviewTraces = () => {
        const traces = [];
        const grouped = {};
        filteredData.forEach(row => {
            const brand = row.brand || 'Unknown';
            if (!grouped[brand]) grouped[brand] = {};
            const key = row[groupKey] || row.week_number;
            if (!grouped[brand][key]) grouped[brand][key] = [];
            grouped[brand][key].push(row.product_price);
        });

        Object.entries(grouped).forEach(([brand, points]) => {
            const keys = Object.keys(points).sort((a, b) => period === 'week' ? Number(a) - Number(b) : a.localeCompare(b));
            traces.push({
                x: keys,
                y: keys.map(k => {
                    const prices = points[k].filter(p => p != null).map(p => Number(p));
                    return prices.length ? prices.reduce((a, b) => a + b, 0) / prices.length : null;
                }),
                mode: 'lines+markers',
                name: brand,
                hovertemplate: `Brand: ${brand}<br>Price: $%{y:.2f}<br>${period === 'day' ? 'Date' : 'Week'}: %{x}<extra></extra>`,
            });
        });
        return traces;
    };

    const latestDate = data.length
        ? data.reduce((max, r) => (r.date > max ? r.date : max), data[0].date || '')
        : null;

    // Toggle brand
    const toggleBrand = (brand) => {
        setSelectedBrands(prev =>
            prev.includes(brand) ? prev.filter(b => b !== brand) : [...prev, brand]
        );
    };

    // Remove brand (tag close)
    const removeBrand = (brand) => {
        setSelectedBrands(prev => prev.filter(b => b !== brand));
    };

    return (
        <Layout>
            <div className="container-fluid py-4">
                {/* ── Header ── */}
                <div className="text-center mb-3 position-relative">
                    <h2 className="fw-bold" style={{ fontSize: '2rem' }}>Competitor Price Monitoring</h2>
                    <small className="text-muted">
                        Last update: {latestDate || 'N/A'}
                    </small>
                    <div className="text-muted small mt-1">Shared Library: all users can view the same baskets.</div>

                    {tokens && (
                        <div className="position-absolute top-0 end-0 p-2 bg-light border rounded shadow-sm d-none d-md-block text-end" style={{ fontSize: '0.8rem', minWidth: '180px' }}>
                            <div className="text-muted fw-semibold">Jungle Scout Budget:</div>
                            <div className="fw-bold text-success">
                                {tokens.remaining} <span className="text-muted fw-normal">/ {tokens.limit} left</span>
                            </div>
                            <div className="progress mt-1" style={{ height: '5px' }}>
                                <div
                                    className="progress-bar bg-success"
                                    style={{ width: `${(tokens.remaining / tokens.limit) * 100}%` }}
                                />
                            </div>
                        </div>
                    )}
                </div>

                {error && <div className="alert alert-danger">{error}</div>}
                
                {syncResult && (
                    <div className={`alert alert-${syncResult.failed > 0 && syncResult.synced === 0 ? 'danger' : 'success'} shadow-sm alert-dismissible fade show`} role="alert">
                        <i className={`bi bi-${syncResult.failed > 0 ? 'exclamation-triangle' : 'check-circle-fill'} me-2`}></i>
                        <strong>Sync completed:</strong> {syncResult.message}
                        <ul className="mt-2 mb-0 mb-1 ps-3" style={{ fontSize: '0.9rem' }}>
                            {syncResult.synced > 0 && <li><strong>{syncResult.synced}</strong> ASINs updated successfully.</li>}
                            {syncResult.skipped > 0 && <li><strong>{syncResult.skipped}</strong> ASINs were already up to date (API call saved).</li>}
                            {syncResult.failed > 0 && <li className="text-danger"><strong>{syncResult.failed}</strong> ASINs failed (check console).</li>}
                        </ul>
                        <button type="button" className="btn-close" onClick={() => setSyncResult(null)} aria-label="Close"></button>
                    </div>
                )}

                {(() => {
                    const normalized = String(error || '').toLowerCase();
                    let hint = '';
                    if (normalized.includes('not subscribed') || normalized.includes('403')) {
                        hint = 'Live provider access is restricted or not subscribed (Error 403). Please verify your RapidAPI subscription or use a historical tracking CSV.';
                    } else if (normalized.includes('too many requests') || normalized.includes('429')) {
                        hint = 'Provider rate limit reached (Error 429). Please wait a few minutes before retrying.';
                    } else if (normalized.includes('external provider failed')) {
                        hint = 'The external data provider is currently unavailable. Try again later or use CSV data.';
                    }
                    return hint && <div className="alert alert-warning shadow-sm"><i className="bi bi-info-circle me-2"></i>{hint}</div>;
                })()}

                {/* ── Basket selector row ── */}
                <div className="d-flex align-items-center gap-3 mb-4 flex-wrap">
                    {selectedBasket && (
                        <span className="badge bg-primary px-3 py-2" style={{ fontSize: '0.9rem' }}>
                            Current basket: <strong>{selectedBasket.id === 'csv' ? `CSV - ${selectedBasket.name}` : selectedBasket.name}</strong>
                            {selectedBasket.country && (
                                <span className="ms-2 badge bg-light text-dark border">
                                    {selectedBasket.country}
                                </span>
                            )}
                            {selectedBasket.id !== 'csv' && (
                                <span className={`ms-2 badge ${selectedBasket.auto_fetch_enabled ? 'bg-success' : 'bg-secondary'}`}>
                                    {selectedBasket.auto_fetch_enabled ? 'Auto-fetch ON' : 'Auto-fetch OFF'}
                                </span>
                            )}
                        </span>
                    )}
                    <div className="dropdown">
                        <button className="btn btn-outline-secondary dropdown-toggle" data-bs-toggle="dropdown">
                            <i className="bi bi-basket2 me-1"></i> Change basket
                        </button>
                        <ul className="dropdown-menu">
                            {baskets.map(b => {
                                const isSelected = selectedBasket?.id === b.id;
                                return (
                                <li key={b.id}>
                                    <button className="dropdown-item d-flex justify-content-between align-items-center"
                                        style={isSelected ? {
                                            backgroundColor: '#eaf2ff',
                                            borderLeft: '3px solid #0d6efd',
                                            color: '#1f2937'
                                        } : undefined}
                                        onClick={() => setSelectedBasket(b)}>
                                        <div className="d-flex flex-column text-start">
                                            <div className="d-flex align-items-center gap-2">
                                                <span>{b.name} ({b.asins?.length || 0} ASINs)</span>
                                                <span className="badge bg-light text-dark border">{b.country || 'US'}</span>
                                            </div>
                                            <small className={b.auto_fetch_enabled ? 'text-success' : 'text-muted'}>
                                                {b.auto_fetch_enabled ? 'Auto-fetch enabled' : 'Auto-fetch disabled'}
                                            </small>
                                        </div>
                                        <div className="d-flex align-items-center gap-2 ms-3">
                                            <i
                                                className={`bi ${b.auto_fetch_enabled ? 'bi-toggle-on text-success' : 'bi-toggle-off text-secondary'}`}
                                                title={b.auto_fetch_enabled ? 'Disable auto-fetch' : 'Enable auto-fetch'}
                                                onClick={(e) => handleToggleAutoFetch(b.id, e)}
                                                style={{ cursor: 'pointer', fontSize: '1rem' }}
                                            ></i>
                                            <i
                                                className="bi bi-pencil-square text-primary"
                                                title="Edit basket"
                                                onClick={(e) => { e.stopPropagation(); openEditBasket(b); }}
                                                style={{ cursor: 'pointer', fontSize: '0.95rem' }}
                                            ></i>
                                            <i
                                                className="bi bi-trash text-danger"
                                                title="Delete basket"
                                                onClick={(e) => { e.stopPropagation(); handleDeleteBasket(b.id); }}
                                                style={{ cursor: 'pointer', fontSize: '0.95rem' }}
                                            ></i>
                                        </div>
                                    </button>
                                </li>
                                );
                            })}
                            <li><hr className="dropdown-divider" /></li>
                            <li><button className="dropdown-item" onClick={() => setShowNewBasket(true)}>
                                <i className="bi bi-plus-lg me-1"></i> Create new basket
                            </button></li>
                        </ul>
                    </div>

                    {selectedBasket && (
                        <div className="d-flex gap-3 align-items-center">
                            <div className="position-relative d-flex align-items-center">
                                <button
                                    className="btn btn-success btn-sm"
                                    onClick={handleFetchData}
                                    disabled={loading}
                                >
                                    {loading ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-arrow-clockwise"></i>}
                                    {' '}{selectedBasket.id === 'csv' ? 'Fetch Data (CSV)' : 'Sync Data'}
                                </button>
                                <div 
                                    className="position-relative ms-2"
                                    onMouseEnter={() => setShowSyncTooltip(true)}
                                    onMouseLeave={() => setShowSyncTooltip(false)}
                                >
                                    <i className="bi bi-info-circle text-primary" style={{ cursor: 'help', fontSize: '0.9rem' }}></i>
                                    {showSyncTooltip && (
                                        <div className="position-absolute bg-dark text-white rounded shadow-sm p-2" 
                                             style={{ bottom: '120%', left: '50%', transform: 'translateX(-50%)', width: '220px', fontSize: '0.75rem', zIndex: 1050, textAlign: 'center' }}>
                                            <strong>Note:</strong> {selectedBasket.id === 'csv' ? 'Fetches current data for the ASINs in your uploaded CSV.' : 'Fetches only missing data (Delta Sync) to avoid unnecessary API requests.'}
                                            <div style={{ position: 'absolute', bottom: '-4px', left: '50%', transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: '5px solid #212529' }}></div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {selectedBasket.id !== 'csv' && (
                                <div className="position-relative d-flex align-items-center">
                                    <button
                                        className="btn btn-warning btn-sm text-dark fw-semibold"
                                        onClick={handleManualBackfill}
                                        disabled={loading || backfilling}
                                    >
                                        {backfilling ? (
                                            <><span className="spinner-border spinner-border-sm me-1" /> Backfilling...</>
                                        ) : (
                                            <><i className="bi bi-cloud-arrow-down-fill me-1"></i> Backfill JS History</>
                                        )}
                                    </button>
                                    <div 
                                        className="position-relative ms-2"
                                        onMouseEnter={() => setShowBackfillTooltip(true)}
                                        onMouseLeave={() => setShowBackfillTooltip(false)}
                                    >
                                        <i className="bi bi-info-circle text-primary" style={{ cursor: 'help', fontSize: '0.9rem' }}></i>
                                        {showBackfillTooltip && (
                                            <div className="position-absolute bg-dark text-white rounded shadow-sm p-2" 
                                                 style={{ bottom: '120%', left: '50%', transform: 'translateX(-50%)', width: '220px', fontSize: '0.75rem', zIndex: 1050, textAlign: 'center' }}>
                                                <strong>Note:</strong> Imports up to 2 years of historical data from Jungle Scout. (Consumes JS tokens).
                                                <div style={{ position: 'absolute', bottom: '-4px', left: '50%', transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: '5px solid #212529' }}></div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {selectedBasket && (
                        <button className="btn btn-outline-primary btn-sm" onClick={handleOpenEditFromSelection}>
                            <i className="bi bi-pencil-square me-1"></i>
                            Edit Basket
                        </button>
                    )}

                    {/* CSV Upload */}
                    <div className="d-flex align-items-center gap-2">
                        <label className="btn btn-outline-info btn-sm mb-0" style={{ cursor: 'pointer' }}>
                            <i className="bi bi-filetype-csv me-1"></i>Upload CSV
                            <input type="file" accept=".csv" hidden onChange={handleCsvUpload} />
                        </label>
                        {csvName && (
                            <span className="badge bg-success d-flex align-items-center gap-1">
                                CSV: {csvName}
                                <i className="bi bi-x-lg" style={{ cursor: 'pointer' }} onClick={handleClearCsv}></i>
                            </span>
                        )}
                    </div>

                    {/* Period toggle moved */}
                </div>

                {/* ── New basket modal ── */}
                {showNewBasket && (
                    <div className="card mb-4 border-primary">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="mb-0">Create New Basket</h6>
                                {tokens && (
                                    <span className="badge bg-light text-dark border" style={{ fontSize: '0.78rem' }}>
                                        Jungle Scout Budget: <strong>{tokens.remaining}</strong> / {tokens.limit} tokens left
                                    </span>
                                )}
                            </div>
                            <form onSubmit={handleCreateBasket}>
                                <div className="row g-2 align-items-center">
                                    <div className="col-md-3">
                                        <input className="form-control form-control-sm" placeholder="Basket name" value={newName} onChange={e => setNewName(e.target.value)} required />
                                    </div>
                                    <div className="col-md-4">
                                        <textarea className="form-control form-control-sm" rows={2} placeholder="ASINs (one per line or comma-separated)" value={newAsins} onChange={e => setNewAsins(e.target.value)} required />
                                    </div>
                                    <div className="col-md-2">
                                        <select className="form-select form-select-sm" value={newCountry} onChange={e => setNewCountry(e.target.value)}>
                                            {['US', 'MX', 'CA', 'UK', 'DE', 'ES', 'FR', 'IT'].map(c =>
                                                <option key={c} value={c}>{c}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="form-check mb-2">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                id="importHistoryCheckbox"
                                                checked={importHistory}
                                                onChange={e => setImportHistory(e.target.checked)}
                                            />
                                            <label className="form-check-label small text-muted" htmlFor="importHistoryCheckbox" style={{ cursor: 'pointer' }}>
                                                Import 2 years history (Jungle Scout API)
                                            </label>
                                        </div>
                                        <div className="d-flex gap-1">
                                            <button className="btn btn-primary btn-sm w-100" type="submit">Create</button>
                                            <button className="btn btn-outline-secondary btn-sm w-100" type="button" onClick={() => { setShowNewBasket(false); setImportHistory(false); }}>Cancel</button>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* ── Edit basket modal ── */}
                {showEditBasket && (
                    <div className="card mb-4 border-warning">
                        <div className="card-body">
                            <h6>Edit Basket</h6>
                            <form onSubmit={handleUpdateBasket}>
                                <div className="row g-2">
                                    <div className="col-md-3">
                                        <input
                                            className="form-control form-control-sm"
                                            placeholder="Basket name"
                                            value={editName}
                                            onChange={e => setEditName(e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-5">
                                        <textarea
                                            className="form-control form-control-sm"
                                            rows={2}
                                            placeholder="ASINs (one per line or comma-separated)"
                                            value={editAsins}
                                            onChange={e => setEditAsins(e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-2">
                                        <select className="form-select form-select-sm" value={editCountry} onChange={e => setEditCountry(e.target.value)}>
                                            {['US', 'MX', 'CA', 'UK', 'DE', 'ES', 'FR', 'IT'].map(c =>
                                                <option key={c} value={c}>{c}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-md-2 d-flex gap-1">
                                        <button className="btn btn-warning btn-sm" type="submit">Save</button>
                                        <button
                                            className="btn btn-outline-secondary btn-sm"
                                            type="button"
                                            onClick={() => {
                                                setShowEditBasket(false);
                                                setEditBasketId(null);
                                                setEditName('');
                                                setEditAsins('');
                                                setEditCountry('US');
                                            }}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* ── Main content (only when data exists) ── */}
                {data.length > 0 && (
                    <>
                        {/* ── Overview Section ── */}
                        <h5 className="fw-bold mb-1">Overview — All Brands</h5>
                        <small className="text-muted d-block mb-3">
                            Use the controls below to filter the overview. The metrics summarize the latest period across selected brands.
                        </small>

                        <div className="row mb-4">
                            {/* Left column: Brand selector + Date filter */}
                            <div className="col-md-4">
                                <div className="mb-3">
                                    <label className="form-label text-muted small">Select the brands to filter the overview chart.</label>
                                    <div className="card">
                                        <div className="card-body py-2 px-3">
                                            <div className="d-flex flex-wrap gap-1 mb-2">
                                                {selectedBrands.map(b => (
                                                    <span key={b} className="badge bg-primary d-flex align-items-center gap-1" style={{ fontSize : '0.78rem', cursor: 'pointer' }}>
                                                        {b}
                                                        <i className="bi bi-x" onClick={() => removeBrand(b)} style={{ cursor: 'pointer' }}></i>
                                                    </span>
                                                ))}
                                            </div>
                                            <div className="dropdown">
                                                <button className="btn btn-outline-secondary btn-sm dropdown-toggle w-100" data-bs-toggle="dropdown">
                                                    <i className="bi bi-gear me-1"></i> Brands to display (overview)
                                                </button>
                                                <ul className="dropdown-menu" style={{ maxHeight: 200, overflowY: 'auto' }}>
                                                    <li><button className="dropdown-item" onClick={() => setSelectedBrands(allBrands)}>Select All</button></li>
                                                    <li><button className="dropdown-item" onClick={() => setSelectedBrands([])}>Clear All</button></li>
                                                    <li><hr className="dropdown-divider" /></li>
                                                    {allBrands.map(b => (
                                                        <li key={b}>
                                                            <button className="dropdown-item d-flex align-items-center gap-2" onClick={() => toggleBrand(b)}>
                                                                <input type="checkbox" checked={selectedBrands.includes(b)} readOnly />
                                                                {b}
                                                            </button>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                // Date filter
                                <div>
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <label className="form-label text-muted small mb-0">
                                            <i className="bi bi-calendar3 me-1"></i> Filter by date (overview)
                                        </label>
                                        <div className="form-check form-switch d-flex align-items-center mb-0">
                                            <input className="form-check-input" type="checkbox" role="switch"
                                                checked={period === 'day'}
                                                onChange={(e) => setPeriod(e.target.checked ? 'day' : 'week')}
                                                id="periodToggle" />
                                            <label className="form-check-label ms-2 small" htmlFor="periodToggle">
                                                {period === 'day' ? '📅 By day' : '📅 By week'}
                                            </label>
                                            <div 
                                                className="position-relative ms-2"
                                                onMouseEnter={() => setShowInfoTooltip(true)}
                                                onMouseLeave={() => setShowInfoTooltip(false)}
                                            >
                                                <i className="bi bi-info-circle text-primary" style={{ cursor: 'help', fontSize: '0.9rem' }}></i>
                                                {showInfoTooltip && (
                                                    <div 
                                                        className="position-absolute bg-dark text-white rounded shadow-sm p-2" 
                                                        style={{ 
                                                            bottom: '120%', 
                                                            left: '50%', 
                                                            transform: 'translateX(-50%)', 
                                                            width: '200px', 
                                                            fontSize: '0.75rem',
                                                            zIndex: 1050,
                                                            textAlign: 'center'
                                                        }}
                                                    >
                                                        <strong>Note:</strong> 'By week' mode groups the chart data and calculates the average product price per week.<br/><br/>
                                                        'By day' mode must be active to filter by specific weeks.
                                                        <div style={{
                                                            position: 'absolute',
                                                            bottom: '-4px',
                                                            left: '50%',
                                                            transform: 'translateX(-50%)',
                                                            width: 0,
                                                            height: 0,
                                                            borderLeft: '5px solid transparent',
                                                            borderRight: '5px solid transparent',
                                                            borderTop: '5px solid #212529'
                                                        }}></div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="d-flex gap-2 mb-2">
                                        <input type="date" className="form-control form-control-sm"
                                            value={dateRange.from}
                                            onChange={e => setDateRange(prev => ({ ...prev, from: e.target.value }))} />
                                        <input type="date" className="form-control form-control-sm"
                                            value={dateRange.to}
                                            onChange={e => setDateRange(prev => ({ ...prev, to: e.target.value }))} />
                                    </div>
                                    <div className="d-flex gap-2">
                                        <select 
                                            className="form-select form-select-sm" 
                                            style={{ maxWidth: '90px' }}
                                            value={activeQuickYear} 
                                            onChange={e => setQuickYear(e.target.value)}
                                        >
                                            {availableYears.map(y => (
                                                <option key={y} value={y}>{y}</option>
                                            ))}
                                        </select>
                                        <select 
                                            className="form-select form-select-sm" 
                                            value={currentWeekValue}
                                            onChange={(e) => {
                                                if (!e.target.value) return;
                                                const [from, to] = e.target.value.split('|');
                                                setDateRange({ from, to });
                                            }}
                                        >
                                            <option value="" disabled>-- Select week --</option>
                                            {filteredWeeks.map(w => (
                                                <option key={w.key} value={`${w.minDate}|${w.maxDate}`}>
                                                    {w.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Right column: Last period highlights — 3×3 grid */}
                            <div className="col-md-8">
                                <h6 className="fw-bold mb-3">Last period highlights</h6>
                                {highlights ? (
                                    <div className="row g-2">
                                        {/* Column 1: Discounts */}
                                        <div className="col-md-4">
                                            <HighlightCard
                                                icon="🏷️" color="#22C55E"
                                                label={`Highest discount — ${highlights.label}`}
                                                sublabel={highlights.highestDisc?.brand}
                                                value={highlights.highestDisc ? `${Number(highlights.highestDisc.discount_pct).toFixed(1)}%` : 'N/A'}
                                            />
                                            <HighlightCard
                                                icon="🏷️" color="#22C55E"
                                                label={`Lowest discount — ${highlights.label}`}
                                                sublabel={highlights.lowestDisc?.brand}
                                                value={highlights.lowestDisc ? `${Number(highlights.lowestDisc.discount_pct).toFixed(1)}%` : 'N/A'}
                                            />
                                            <HighlightCard
                                                icon="🏷️" color="#22C55E"
                                                label={`Average discount — ${highlights.label}`}
                                                value={highlights.avgDiscount != null ? `${Number(highlights.avgDiscount).toFixed(1)}%` : 'N/A'}
                                            />
                                        </div>
                                        {/* Column 2: Prices */}
                                        <div className="col-md-4">
                                            <HighlightCard
                                                icon="💲" color="#3B82F6"
                                                label={`Highest price — ${highlights.label}`}
                                                sublabel={highlights.highestPrice?.brand}
                                                value={highlights.highestPrice && highlights.highestPrice.product_price != null ? `$${Number(highlights.highestPrice.product_price).toFixed(2)}` : 'N/A'}
                                            />
                                            <HighlightCard
                                                icon="💲" color="#3B82F6"
                                                label={`Lowest price — ${highlights.label}`}
                                                sublabel={highlights.lowestPrice?.brand}
                                                value={highlights.lowestPrice && highlights.lowestPrice.product_price != null ? `$${Number(highlights.lowestPrice.product_price).toFixed(2)}` : 'N/A'}
                                            />
                                            <HighlightCard
                                                icon="💲" color="#3B82F6"
                                                label={`Average price — ${highlights.label}`}
                                                value={highlights.avgPrice != null ? `$${Number(highlights.avgPrice).toFixed(2)}` : 'N/A'}
                                            />
                                        </div>
                                        {/* Column 3: Price Changes */}
                                        <div className="col-md-4">
                                            <HighlightCard
                                                icon="🔺" color="#EF4444"
                                                label={`Largest price change — ${highlights.label}`}
                                                sublabel={highlights.largestChange?.brand}
                                                value={highlights.largestChange ? `${Number(highlights.largestChange.price_change) > 0 ? '+' : ''}${Number(highlights.largestChange.price_change).toFixed(1)}%` : 'N/A'}
                                            />
                                            <HighlightCard
                                                icon="🔻" color="#EF4444"
                                                label={`Lowest price change — ${highlights.label}`}
                                                sublabel={highlights.lowestChange?.brand}
                                                value={highlights.lowestChange ? `${Number(highlights.lowestChange.price_change) > 0 ? '+' : ''}${Number(highlights.lowestChange.price_change).toFixed(1)}%` : 'N/A'}
                                            />
                                            <HighlightCard
                                                icon="↕" color="#F59E0B"
                                                label={`Average price change — ${highlights.label}`}
                                                value={highlights.avgChange != null ? `${Number(highlights.avgChange) > 0 ? '+' : ''}${Number(highlights.avgChange).toFixed(1)}%` : 'N/A'}
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-muted">No data for the selected period.</p>
                                )}
                            </div>
                        </div>

                        {/* ── Overview Chart ── */}
                        <div className="card mb-4">
                            <div className="card-body">
                                <Plot
                                    data={buildOverviewTraces()}
                                    layout={{
                                        title: `Overview — ${period === 'day' ? 'Daily' : 'Weekly'} Price by Brand`,
                                        height: 420,
                                        hovermode: 'x unified',
                                        legend: { title: { text: 'Brand' } },
                                        margin: { l: 60, r: 20, t: 50, b: 55 },
                                        xaxis: { title: { text: period === 'day' ? 'Date' : 'Week Number' }, ...(period === 'day' ? { type : 'date', tickformat: '%b %d' } : { type: 'category', tickprefix: 'Wk ' }) },
                                        yaxis: { title: { text: 'Product Price (USD)' }, tickprefix: '$' },
                                    }}
                                    config={{ responsive: true }}
                                    style={{ width: '100%' }}
                                />
                            </div>
                        </div>

                        {/* ── Product Gallery ── */}
                        {(() => {
                            const latestRows = latestDate ? data.filter(r => r.date === latestDate) : [];

                            // Dedup by ASIN (prefer record with price if multiple exist)
                            const uniqueRows = Array.from(
                                latestRows.reduce((map, row) => {
                                    if (!map.has(row.asin) || (row.product_price != null && map.get(row.asin).product_price == null)) {
                                        map.set(row.asin, row);
                                    }
                                    return map;
                                }, new Map()).values()
                            ).sort((a, b) => a.asin.localeCompare(b.asin));

                            return uniqueRows.length > 0 ? (
                                <div className="card">
                                    <div className="card-body">
                                        <h5 className="card-title">Current Products</h5>
                                        <div className="row g-3">
                                            {uniqueRows.map(row => (
                                                <div key={row.asin} className="col-md-3 col-sm-6">
                                                    <ProductCard row={row} />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ) : null;
                        })()}
                    </>
                )}

                {/* Empty states */}
                {data.length === 0 && !loading && selectedBasket && (
                    <div className="text-center text-muted py-5">
                        <h5>No data yet</h5>
                        <p>Click <strong>Fetch Data</strong> to get competitor data from Amazon</p>
                    </div>
                )}

                {!selectedBasket && (
                    <div className="text-center text-muted py-5">
                        <i className="bi bi-basket2 display-4 d-block mb-3"></i>
                        <h5>Select or create a basket to start monitoring</h5>
                    </div>
                )}
            </div>

            {showBackfillModal && (
                <div style={{
                    position: 'fixed', inset: 0, zIndex: 9999,
                    background: 'rgba(8,12,24,0.72)', backdropFilter: 'blur(6px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <div style={{
                        background: '#ffffff', borderRadius: '20px', padding: '36px 32px 28px',
                        maxWidth: '440px', width: '90%',
                        boxShadow: '0 32px 80px rgba(0,0,0,0.22), 0 0 0 1px rgba(0,0,0,0.06)',
                    }}>
                        {/* Icon */}
                        <div className="text-center mb-4">
                            <div style={{
                                width: '52px', height: '52px', borderRadius: '14px',
                                background: 'linear-gradient(145deg, #1e293b, #334155)',
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: '0 4px 16px rgba(30,41,59,0.35)',
                            }}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 3v12m0 0l-4-4m4 4l4-4" />
                                    <path d="M3 17v1a3 3 0 003 3h12a3 3 0 003-3v-1" />
                                </svg>
                            </div>
                        </div>

                        {/* Title */}
                        <h5 style={{ fontWeight: 700, fontSize: '1.15rem', textAlign: 'center', marginBottom: '6px', color: '#0f172a' }}>
                            Import Jungle Scout History
                        </h5>
                        <p style={{ textAlign: 'center', color: '#64748b', fontSize: '0.875rem', marginBottom: '24px', lineHeight: 1.6 }}>
                            Up to <strong style={{ color: '#0f172a' }}>2 years</strong> of sales and price history will be imported for basket <strong style={{ color: '#0f172a' }}>"{selectedBasket?.name}"</strong>.
                        </p>

                        {/* Token budget */}
                        {tokens && (
                            <div style={{
                                background: '#f8fafc', border: '1px solid #e2e8f0',
                                borderRadius: '12px', padding: '14px 16px', marginBottom: '24px',
                            }}>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <span style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 500 }}>Available tokens</span>
                                    <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#0f172a' }}>
                                        {tokens.remaining} <span style={{ color: '#94a3b8', fontWeight: 400 }}>/ {tokens.limit}</span>
                                    </span>
                                </div>
                                <div style={{ height: '5px', background: '#e2e8f0', borderRadius: '99px', overflow: 'hidden' }}>
                                    <div style={{
                                        width: `${Math.round((tokens.remaining / tokens.limit) * 100)}%`,
                                        height: '100%',
                                        background: tokens.remaining < tokens.limit * 0.25
                                            ? 'linear-gradient(90deg, #ef4444, #f87171)'
                                            : 'linear-gradient(90deg, #22c55e, #4ade80)',
                                        borderRadius: '99px',
                                        transition: 'width 0.4s ease',
                                    }} />
                                </div>
                                <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '10px', marginBottom: 0 }}>
                                    This action will consume <strong style={{ color: '#475569' }}>1 token per ASIN</strong> in the basket.
                                </p>
                            </div>
                        )}

                        {/* Actions */}
                        <div className="d-flex gap-2">
                            <button
                                style={{
                                    flex: 1, padding: '10px', borderRadius: '10px',
                                    border: '1px solid #e2e8f0', background: '#f8fafc',
                                    color: '#475569', fontWeight: 500, fontSize: '0.875rem', cursor: 'pointer',
                                }}
                                onClick={() => setShowBackfillModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                style={{
                                    flex: 1, padding: '10px', borderRadius: '10px',
                                    border: 'none', background: 'linear-gradient(135deg, #1e293b, #334155)',
                                    color: '#ffffff', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer',
                                    boxShadow: '0 2px 8px rgba(30,41,59,0.3)',
                                }}
                                onClick={confirmBackfill}
                            >
                                Confirm Import
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Backfill Result Toast ── */}
            {backfillResult && (
                <div style={{
                    position: 'fixed', bottom: '24px', right: '24px', zIndex: 9999,
                    background: backfillResult.success ? '#ecfdf5' : '#fef2f2',
                    border: `1px solid ${backfillResult.success ? '#6ee7b7' : '#fca5a5'}`,
                    borderRadius: '12px', padding: '14px 18px', maxWidth: '360px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    display: 'flex', alignItems: 'flex-start', gap: '10px',
                }}>
                    <span style={{
                        display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', marginTop: '4px', flexShrink: 0,
                        background: backfillResult.success ? '#10b981' : '#ef4444'
                    }} />
                    <div className="flex-grow-1">
                        <div className="fw-semibold small">{backfillResult.success ? 'Import completed' : 'Import failed'}</div>
                        <div className="small text-muted mt-1">{backfillResult.message}</div>
                    </div>
                    <button className="btn-close" style={{ fontSize: '10px' }} onClick={() => setBackfillResult(null)} />
                </div>
            )}

        </Layout>
    );
}


/* ── Highlight metric card ── */
function HighlightCard({ icon, color, label, sublabel, value }) {
    return (
        <div className="mb-2 p-2 rounded" style={{ background: '#f8f9fa', borderLeft: `3px solid ${color}` }}>
            <div style={{ fontSize: '0.7rem', color: '#666' }}>
                {icon} {label}{sublabel ? ` — ${sublabel}` : ''}
            </div>
            <div className="fw-bold" style={{ fontSize: '1.3rem' }}>{value}</div>
        </div>
    );
}

export default TrackingOverview;
